/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateVatRequest } from '../models/create-vat-request';
import { GenericResponse } from '../models/generic-response';
import { UpdateVatRequest } from '../models/update-vat-request';
import { VatResponse } from '../models/vat-response';

/**
 * Vat APIs
 */
@Injectable({ providedIn: 'root' })
export class VatService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllVat()` */
  static readonly GetAllVatPath = '/api/v1/vat';

  /**
   * Get list of all VATs.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllVat()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllVat$Response(
    params?: {
      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<VatResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VatService.GetAllVatPath,
      'get'
    );
    if (params) {
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<VatResponse>;
        })
      );
  }

  /**
   * Get list of all VATs.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllVat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllVat(
    params?: {
      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<VatResponse> {
    return this.getAllVat$Response(params, context).pipe(
      map((r: StrictHttpResponse<VatResponse>): VatResponse => r.body)
    );
  }

  /** Path part for operation `createVat()` */
  static readonly CreateVatPath = '/api/v1/vat';

  /**
   * Create new VAT.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createVat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createVat$Response(
    params: {
      body: CreateVatRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<VatResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VatService.CreateVatPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<VatResponse>;
        })
      );
  }

  /**
   * Create new VAT.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createVat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createVat(
    params: {
      body: CreateVatRequest;
    },
    context?: HttpContext
  ): Observable<VatResponse> {
    return this.createVat$Response(params, context).pipe(
      map((r: StrictHttpResponse<VatResponse>): VatResponse => r.body)
    );
  }

  /** Path part for operation `getVatById()` */
  static readonly GetVatByIdPath = '/api/v1/vat/{id}';

  /**
   * Get vat details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getVatById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getVatById$Response(
    params: {
      /**
       * The vat's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<VatResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VatService.GetVatByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<VatResponse>;
        })
      );
  }

  /**
   * Get vat details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getVatById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getVatById(
    params: {
      /**
       * The vat's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<VatResponse> {
    return this.getVatById$Response(params, context).pipe(
      map((r: StrictHttpResponse<VatResponse>): VatResponse => r.body)
    );
  }

  /** Path part for operation `deleteVat()` */
  static readonly DeleteVatPath = '/api/v1/vat/{id}';

  /**
   * Delete VAT by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteVat()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteVat$Response(
    params: {
      /**
       * The VAT id that needs to be disabled
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VatService.DeleteVatPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete VAT by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteVat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteVat(
    params: {
      /**
       * The VAT id that needs to be disabled
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteVat$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateVat()` */
  static readonly UpdateVatPath = '/api/v1/vat/{id}';

  /**
   * Update VAT by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateVat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateVat$Response(
    params: {
      /**
       * The VAT type's id that needs to be updated
       */
      id: number;
      body: UpdateVatRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<VatResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VatService.UpdateVatPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<VatResponse>;
        })
      );
  }

  /**
   * Update VAT by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateVat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateVat(
    params: {
      /**
       * The VAT type's id that needs to be updated
       */
      id: number;
      body: UpdateVatRequest;
    },
    context?: HttpContext
  ): Observable<VatResponse> {
    return this.updateVat$Response(params, context).pipe(
      map((r: StrictHttpResponse<VatResponse>): VatResponse => r.body)
    );
  }
}
